import {
  FREE_TIER_TRACKED_PROPERTIES_LIMIT,
  RecurringTypesEnum,
  SubscriptionProductNamesEnum,
} from "dash-common";
import { SubscriptionProduct } from "gql/generated.graphql";
import { Text as DashText, UpgradeSubscriptionModalType } from "dash-ui";
import { TFunction } from "i18next";
import { DefaultTheme } from "styled-components";
import { FilterSubscriptionsOptions } from "./UpgradeSubscriptionModalV3.interfaces";

export function getModalContent(
  type: UpgradeSubscriptionModalType,
  t: TFunction<"pages", undefined>,
  theme: DefaultTheme,
  isSeeker?: boolean
) {
  switch (type) {
    case UpgradeSubscriptionModalType.PORTFOLIO:
      return {
        title: t("pages:profilePage.billing.upgradePortfolioPlanModalTitle"),
        // subtitle: (
        //   <DashText color={theme.colors.n80}>
        //     {t("pages:profilePage.billing.upgradePlanModalPortfolioSubtitle")}
        //   </DashText>
        // ),
      };
    case UpgradeSubscriptionModalType.DOWNLOAD:
      return {
        title: t("pages:profilePage.billing.upgradeDownloadPlanModalTitle"),
        subtitle: (
          <DashText color={theme.colors.n80}>
            {t("pages:profilePage.billing.upgradePlanModalDownloadSubtitle")}
          </DashText>
        ),
      };
    case UpgradeSubscriptionModalType.COMPLETE_ACCOUNT:
      return {
        title: t(
          "pages:profilePage.billing.upgradeCompleteAccountPlanModalTitle"
        ),
        // subtitle: (
        //   <DashText color={theme.colors.n80}>
        //     You have <b>free</b> access to the details of a <b>limited</b>{" "}
        //     number of properties.
        //     <br /> Start your Free Trial right now to get full access to{" "}
        //     <b>400+ New Developments</b>
        //     {isSeeker ? "" : " to boost your sales"}!
        //   </DashText>
        // ),
      };

    case UpgradeSubscriptionModalType.SAVED_PROPERTIES:
      return {
        title: t(
          "pages:profilePage.billing.upgradeSavedPropertiesPlanModalTitle"
        ),
        subtitle: (
          <DashText color={theme.colors.n80}>
            {t(
              "pages:profilePage.billing.upgradePlanModalSavedPropertiesSubtitle",
              { limit: FREE_TIER_TRACKED_PROPERTIES_LIMIT }
            )}
          </DashText>
        ),
      };

    case UpgradeSubscriptionModalType.IDX:
      return {
        title: t("pages:profilePage.billing.upgradeExchangePlanModalTitle"),
        // subtitle: (
        //   <DashText color={theme.colors.n80}>
        //     With DASH eXchange, you can easily add 400+ new developments
        //     anywhere on your website.
        //     <br />
        //     Upgrade your subscription now to boost your listings with DASH's
        //     data.
        //   </DashText>
        // ),
      };

    default:
      return {
        title: t("pages:profilePage.billing.upgradePlanModalTitle"),
        subtitle: <></>,
      };
  }
}

/**
 * Filters subscriptions by recurring type and user seeker status.
 * Returns products matching the recurring type or FREE products.
 */
function filterByRecurringAndSeeker({
  subscriptions,
  isSeeker,
  recurringType,
}: FilterSubscriptionsOptions): SubscriptionProduct[] {
  return subscriptions.filter(
    (product) =>
      ((isSeeker
        ? product.name === SubscriptionProductNamesEnum.DIRECT
        : product.name !== SubscriptionProductNamesEnum.DIRECT) &&
        product.recurringType === recurringType) ||
      product.recurringType === RecurringTypesEnum.FREE
  );
}

/**
 * Hides legacy prices from new users, and hides the new variant of the same
 * tier when the user is grandfathered on a legacy price (so they don't see
 * two cards for the same product). The user's legacy current plan itself is
 * re-injected by `ensureCurrentPlanIncluded`.
 */
function applyLegacyFilter(
  filtered: SubscriptionProduct[],
  options: FilterSubscriptionsOptions
): SubscriptionProduct[] {
  const { currentUserProduct, currentProductId, recurringType } = options;
  const legacyGrandfatheredName =
    currentUserProduct?.legacy &&
    currentUserProduct?.recurringType === recurringType
      ? currentUserProduct.name
      : null;

  return filtered.filter((product) => {
    if (product.legacy && product._id !== currentProductId) {
      return false;
    }

    if (
      !product.legacy &&
      legacyGrandfatheredName &&
      product.name === legacyGrandfatheredName &&
      product.recurringType === recurringType
    ) {
      return false;
    }

    return true;
  });
}

/**
 * Applies modal-specific filtering rules to show relevant plans.
 * Skips filtering when showAllPlans is true or modalType is not specified.
 */
function applyModalTypeFilter(
  filtered: SubscriptionProduct[],
  options: FilterSubscriptionsOptions
): SubscriptionProduct[] {
  const { modalType, showAllPlans } = options;

  // Skip modal type filtering if showing all plans
  if (!modalType || showAllPlans) {
    return filtered;
  }

  switch (modalType) {
    case UpgradeSubscriptionModalType.DOWNLOAD:
    case UpgradeSubscriptionModalType.PORTFOLIO:
      // Show only PRO and Exchange plans
      return filtered.filter(
        (product) =>
          product.name === SubscriptionProductNamesEnum.PRO ||
          product.name === SubscriptionProductNamesEnum.EXCHANGE
      );

    case UpgradeSubscriptionModalType.IDX:
      // Show only Exchange plan
      return filtered.filter(
        (product) => product.name === SubscriptionProductNamesEnum.EXCHANGE
      );

    case UpgradeSubscriptionModalType.COMPLETE_ACCOUNT:
    case UpgradeSubscriptionModalType.GENERAL:
    default:
      // Show all plans (no additional filtering)
      return filtered;
  }
}

/**
 * Ensures user's current plan appears in results even if filtered out by other rules.
 * Adds the current plan at the beginning of the list if not already present.
 */
function ensureCurrentPlanIncluded(
  filtered: SubscriptionProduct[],
  options: FilterSubscriptionsOptions
): SubscriptionProduct[] {
  const { subscriptions, currentProductId, currentUserProduct, showAllPlans } =
    options;

  // Don't inject current plan when showing all plans or if no current product
  if (!currentProductId || showAllPlans) {
    return filtered;
  }

  // Check if current product is already in the filtered list
  if (filtered.find((p) => p._id === currentProductId)) {
    return filtered;
  }

  // First try to find in all subscriptions array
  let currentProduct = subscriptions.find((p) => p._id === currentProductId);

  // If not found in array but we have the currentUserProduct, use that
  if (!currentProduct && currentUserProduct) {
    currentProduct = {
      ...currentUserProduct,
      _id: currentProductId, // Ensure the _id matches
    };
  }

  // Add current product at the beginning if found
  if (currentProduct) {
    return [currentProduct, ...filtered];
  }

  return filtered;
}

/**
 * Filters subscription products based on user context and modal type.
 * Applies filtering pipeline: recurring/seeker -> legacy -> modal type -> current plan injection.
 */
export function filterSubscriptions(
  options: FilterSubscriptionsOptions
): SubscriptionProduct[] {
  let filtered = filterByRecurringAndSeeker(options);
  filtered = applyLegacyFilter(filtered, options);
  filtered = applyModalTypeFilter(filtered, options);
  filtered = ensureCurrentPlanIncluded(filtered, options);
  return filtered;
}

export function getSkeletonCount(
  modalType: UpgradeSubscriptionModalType,
  isSeeker?: boolean,
  showAllPlans?: boolean
): number {
  // If showing all plans, always show 3 (or 1 for seekers)
  if (showAllPlans) {
    return isSeeker ? 1 : 3;
  }

  // Seekers always see 1 card (Direct)
  if (isSeeker) return 1;

  // Modal type determines card count
  switch (modalType) {
    case UpgradeSubscriptionModalType.IDX:
      return 1; // Only Exchange

    case UpgradeSubscriptionModalType.DOWNLOAD:
    case UpgradeSubscriptionModalType.PORTFOLIO:
      return 2; // PRO and Exchange

    case UpgradeSubscriptionModalType.COMPLETE_ACCOUNT:
    case UpgradeSubscriptionModalType.GENERAL:
    default:
      return 3; // All plans
  }
}

export function getSkeletonVariant(
  modalType: UpgradeSubscriptionModalType,
  isSeeker: boolean | undefined,
  index: number
): "basic" | "premium" {
  // First card is "basic" only when showing all plans or for seekers
  if (index === 0) {
    const showAllPlans =
      !modalType ||
      modalType === UpgradeSubscriptionModalType.COMPLETE_ACCOUNT ||
      modalType === UpgradeSubscriptionModalType.GENERAL ||
      modalType === UpgradeSubscriptionModalType.SAVED_PROPERTIES;

    return showAllPlans || isSeeker ? "basic" : "premium";
  }

  return "premium";
}
